import { FlaskConical, CheckCircle, ClipboardList, MessageSquare, Play } from 'lucide-react';
import type { ActivityEntry } from '@/services/studentProgressService';

interface Props {
  activities: ActivityEntry[];
}

const activityIcons: Record<string, { icon: React.ReactNode; color: string }> = {
  experiment_started: {
    icon: <Play size={14} />,
    color: 'var(--primary, #FF5A36)',
  },
  step_completed: {
    icon: <CheckCircle size={14} />,
    color: 'var(--secondary, #00A86B)',
  },
  quiz_completed: {
    icon: <ClipboardList size={14} />,
    color: 'var(--accent, #FFEA00)',
  },
  feedback_generated: {
    icon: <MessageSquare size={14} />,
    color: '#3B82F6',
  },
};

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function ActivityTimeline({ activities }: Props) {
  if (activities.length === 0) {
    return (
      <div className="timeline-empty">
        <FlaskConical size={32} />
        <p>No activity yet. Start an experiment to begin tracking your progress.</p>
        <style>{timelineStyles}</style>
      </div>
    );
  }

  return (
    <div className="activity-timeline">
      {activities.map((entry, idx) => {
        const config = activityIcons[entry.activityType] || {
          icon: <FlaskConical size={14} />,
          color: 'var(--text-muted, #999999)',
        };

        return (
          <div key={entry.id || idx} className="timeline-item">
            <div className="timeline-line-wrapper">
              <div
                className="timeline-icon"
                style={{
                  background: `${config.color}22`,
                  color: config.color,
                  border: `2px solid ${config.color}44`,
                }}
              >
                {config.icon}
              </div>
              {idx < activities.length - 1 && <div className="timeline-connector" />}
            </div>
            <div className="timeline-content">
              <p className="timeline-description">{entry.description}</p>
              <div className="timeline-meta">
                {entry.experimentName && (
                  <span className="timeline-experiment">
                    Exp {entry.experimentNumber}: {entry.experimentName}
                  </span>
                )}
                <span className="timeline-time">{formatDate(entry.createdAt)}</span>
              </div>
            </div>
          </div>
        );
      })}
      <style>{timelineStyles}</style>
    </div>
  );
}

const timelineStyles = `
  .timeline-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 32px 16px;
    color: var(--text-muted, #999999);
    text-align: center;
  }
  .timeline-empty p {
    font-size: 13px;
    max-width: 280px;
    line-height: 1.5;
    margin: 0;
  }
  .activity-timeline {
    display: flex;
    flex-direction: column;
  }
  .timeline-item {
    display: flex;
    gap: 12px;
    min-height: 56px;
  }
  .timeline-line-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
    width: 32px;
  }
  .timeline-icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .timeline-connector {
    width: 2px;
    flex: 1;
    background: var(--border, #4A4A4A);
    margin: 4px 0;
    min-height: 12px;
  }
  .timeline-content {
    flex: 1;
    padding-bottom: 16px;
    min-width: 0;
  }
  .timeline-description {
    font-size: 13px;
    color: var(--text-primary, #FFFFFF);
    margin: 0 0 4px 0;
    line-height: 1.4;
    padding-top: 6px;
  }
  .timeline-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }
  .timeline-experiment {
    font-size: 11px;
    color: var(--text-muted, #999999);
    background: var(--bg-dark, #1A1A1A);
    padding: 2px 8px;
    border-radius: 10px;
  }
  .timeline-time {
    font-size: 11px;
    color: var(--text-muted, #999999);
  }
`;
